<template>
    <b-modal
        lazy
        title-tag="h2"
        title="Add Snippet"
        :visible="isOpen"
        footer-class="d-flex justify-content-between"
        @hide="modalClosed"
    >
        <p>
            <strong>TIP:</strong> Rather than adding content from here, use a
            hashtag shortcut to add the content directly into the desired field.
        </p>
        <div
            v-for="lockedElement in filteredLockedElements"
            :key="lockedElement.id"
            class="alert alert-warning mb-2"
            role="alert"
        >
            <strong>{{ lockedElement.label }}</strong> option is disabled
            because {{ lockedElement.name }} is working on the same field
        </div>
        <form ref="snippetForm">
            <div class="form-group row">
                <label for="content" class="col-sm-4 col-form-label"
                    >Content:</label
                >
                <div class="col-sm-8">
                    <v-select
                        id="content"
                        name="content"
                        :options="contentOptions"
                        :class="{ 'border-danger': errors.has('content') }"
                        :placeholder="
                            optionLoading.content
                                ? 'Loading...'
                                : 'Content (required)'
                        "
                        :loading="optionLoading.content"
                        :clearable="false"
                        label="name"
                        class="show-actions"
                        v-model="content"
                        v-validate="'required'"
                    />
                </div>
            </div>
            <div class="form-group row">
                <label for="section" class="col-sm-4 col-form-label"
                    >Add To:</label
                >
                <div class="col-sm-8">
                    <v-select
                        id="section"
                        name="section"
                        :options="sectionOptions"
                        :class="{ 'border-danger': errors.has('section') }"
                        :placeholder="
                            optionLoading.section
                                ? 'Loading...'
                                : 'Section (required)'
                        "
                        :selectable="
                            option => !checkIsOptionDisabled(option.id)
                        "
                        :loading="optionLoading.section"
                        :clearable="false"
                        :searchable="false"
                        v-model="section"
                        v-validate="'required'"
                        item-text="label"
                        class="show-actions"
                    />
                </div>
            </div>
        </form>
        <template v-slot:modal-footer="{ cancel }">
            <modal-footer
                :is-disabled="disabledAddButton"
                :is-loading="isLoading"
                ok-title="Add"
                @ok-action="addSnippet"
                @cancel-action="cancel"
            />
        </template>
    </b-modal>
</template>
<script type="text/javascript">
import { map, filter, find, some, has } from "lodash";
import SoapServices from "@/services/patient/patient-soap.js";
import TemplatesService from "@/services/templates";
import io from "socket.io-client";
import SoapContentSocketMixin from "@/mixins/soap-content-socket.js";

export default {
    mixins: [SoapContentSocketMixin],
    components: {
        ModalFooter: () => import("@/components/elements/modal-footer"),
        VSelect: () => import("vue-select")
    },
    watch: {
        isOpen(value) {
            this.$store.commit("soap/setIsAddSnippetOpened", value);
        },
        isSoapContentHasDiagnoses(value) {
            this.filterDiagnosesSection();
        },
        section(value, oldValue) {
            // When close modal, watch also will be triggered but the soapSocket will be null
            if (this.soapSocket) {
                // When user change tab and other user select/focus on the selected option
                // Then the user return to the tab, it will re-select the option but unable to add the snippet due to other user currently using the selected option
                if (
                    this.previousSectionBeforeHidden &&
                    this.disabledAddBasedOnLockedElements
                ) {
                    // reset the previousSectionBeforeHidden so that after this, user change the option will trigger below codes as usual
                    let isUserChangingTabAndSectionSetToNull =
                        this.section === null;

                    if (!isUserChangingTabAndSectionSetToNull) {
                        this.previousSectionBeforeHidden = null;
                    }
                    return;
                }

                if (value) {
                    this.soapSocket.emit("medical-note", {
                        field: value.id,
                        action: "focus",
                        by: this.$root.currentActor.id,
                        name: this.$root.currentActor.fullname
                    });
                }

                // To remove focus when change value
                // !this.disabledAddBasedOnLockedElements when user changed value after visibility "visible"
                if (oldValue && !this.disabledAddBasedOnLockedElements) {
                    this.triggerOutFocus(oldValue.id, false);
                }

                this.$store.commit(
                    "soap/setCurrentFocusedElementId",
                    value?.id || null
                );

                this.disabledAddBasedOnLockedElements = false;
            }
        },
        filteredLockedElements(data) {
            if (
                this.disabledAddBasedOnLockedElements &&
                ((this.section &&
                    !find(this.filteredLockedElements, {
                        id: this.section.id
                    })) ||
                    (this.previousSectionBeforeHidden &&
                        !find(this.filteredLockedElements, {
                            id: this.previousSectionBeforeHidden.id
                        })))
            ) {
                // When the user change tab and back to the tab, the option will auto select previous selection even the option already been selected by other user
                // And the the user change the section selection, should enable back the add button and set the locked element focus to other user
                this.disabledAddBasedOnLockedElements = false;
                this.soapSocket.emit("medical-note", {
                    field: this.section.id,
                    action: "focus",
                    by: this.$root.currentActor.id,
                    name: this.$root.currentActor.fullname
                });

                this.$store.commit(
                    "soap/setCurrentFocusedElementId",
                    this.section.id
                );
            }
        }
    },
    data() {
        return {
            isOpen: false,
            optionLoading: {
                content: true,
                section: true
            },
            contentOptions: [],
            sectionOptions: [],
            content: null,
            section: null,
            isLoading: false,
            soapSocket: null,
            previousSectionBeforeHidden: null,
            disabledAddBasedOnLockedElements: false,
            disabledAddDueBeenLocked: false
        };
    },
    computed: {
        soapId() {
            return this.soap ? this.soap.id : null;
        },
        soap() {
            return this.$store.getters["soap/soapDetails"];
        },
        isSoapContentHasDiagnoses() {
            return this.$store.getters["soap/isSoapContentHasDiagnoses"];
        },
        disabledAddButton() {
            return (
                Object.values(this.optionLoading).some(el => el) ||
                this.disabledAddBasedOnLockedElements ||
                this.disabledAddDueBeenLocked
            );
        },
        lockedElements() {
            return this.$store.getters["soap/lockedElements"] || [];
        },
        sectionOptionsLabelId() {
            // Set as a computed to reduce the memory usage each time the filteredLockedElements changed
            return this.sectionOptions.reduce((acc, option) => {
                acc[option.id] = option.label;
                return acc;
            }, {});
        },
        filteredLockedElements() {
            const lockedElements = this.lockedElements;
            return lockedElements.reduce((filtered, element) => {
                if (has(this.sectionOptionsLabelId, element.id)) {
                    filtered.push({
                        ...element,
                        label: this.sectionOptionsLabelId[element.id]
                    });
                }
                return filtered;
            }, []);
        },
        soapChannel() {
            return "medical-note:" + this.soapId;
        }
    },
    beforeDestroy() {},
    methods: {
        triggerVisibilityChange(e) {
            if (document.visibilityState === "visible") {
                if (
                    this.previousSectionBeforeHidden &&
                    find(this.filteredLockedElements, {
                        id: this.previousSectionBeforeHidden.id
                    })
                ) {
                    this.disabledAddBasedOnLockedElements = true;
                }
                this.section = this.previousSectionBeforeHidden;
            } else if (document.visibilityState === "hidden") {
                this.previousSectionBeforeHidden = this.section;
                this.section = null;
            }
        },
        beforeWindowUnload(e) {
            // To remove focus when the user reload page or close the page
            // Use currentFocusedElementId as same reason from function modalClosed()
            if (this.section) {
                this.triggerOutFocus(this.currentFocusedElementId);
            }
        },
        getElementLabel(lockedElement) {
            return find(this.sectionOptions, { id: lockedElement.id })?.label;
        },
        checkIsOptionDisabled(optionId) {
            return (
                this.filteredLockedElements &&
                some(this.filteredLockedElements, { id: optionId })
            );
        },
        triggerOutFocus(selectedSectionId, removeFromFocusedElementId = true) {
            if (selectedSectionId) {
                this.soapSocket.emit("medical-note", {
                    field: selectedSectionId,
                    action: "out-focus",
                    by: this.$root.currentActor.id,
                    name: this.$root.currentActor.fullname
                });
            }

            if (removeFromFocusedElementId) {
                // To make sure that the socket in soap-content.vue not trigger focus for the currentFocusedElementId if this not removed
                this.$store.commit("soap/setCurrentFocusedElementId", null);
            }
        },
        modalClosed() {
            // To remove the focus upon close the modal
            // Use this.currentFocusedElementId as it will be null for the scenario from triggerVisibilityChange as the selected Add To will not be focused when other user currently locked the element
            this.triggerOutFocus(this.currentFocusedElementId);

            window.removeEventListener("beforeunload", this.beforeWindowUnload);
            document.removeEventListener(
                "visibilitychange",
                this.triggerVisibilityChange
            );
            this.disconnectSoapSocket();
        },
        disconnectSoapSocket() {
            if (this.soapSocket) {
                this.soapSocket.off(this.soapChannel, this.handleSocketMessage);
                this.soapSocket.disconnect();
                this.soapSocket = null;
            }
        },
        async open() {
            let self = this;
            self.isOpen = true;

            this.loadContentOptions();
            this.loadSectionOptions();

            this.$nextTick(() => {
                this.soapSocket = io.connect(process.env.SOCKET_URL, {
                    secure: true,
                    query: {
                        soapChannel: this.soapChannel,
                        soapId: this.soapId,
                        userId: this.$root.currentActor.id
                    }
                });
                this.soapSocket.on(this.soapChannel, this.handleSocketMessage);
            });

            // To handle the page refresh
            window.addEventListener("beforeunload", this.beforeWindowUnload);
            // To handle change tab
            document.addEventListener(
                "visibilitychange",
                this.triggerVisibilityChange
            );
        },
        loadContentOptions() {
            this.optionLoading.content = true;
            TemplatesService.getAllSnippet(!TemplatesService.IS_FILTER_HASHTAG)
                .then(response => {
                    if (
                        response &&
                        response.resource &&
                        response.resource.data
                    ) {
                        this.contentOptions = response.resource.data;
                        this.content = this.contentOptions?.[0];
                    }
                })
                .finally(() => {
                    this.optionLoading.content = false;
                });
        },
        async loadSectionOptions() {
            this.optionLoading.section = true;
            SoapServices.addSectionSnippet(this.soapId)
                .then(response => {
                    if (
                        response &&
                        response.response &&
                        response.response.elements
                    ) {
                        this.sectionOptions = map(
                            response.response.elements,
                            (label, id) => {
                                return {
                                    id,
                                    label
                                };
                            }
                        );
                        this.filterDiagnosesSection(false);
                        this.setSection();
                    }
                })
                .finally(() => {
                    this.optionLoading.section = false;
                });
        },
        setSection() {
            const filteredLockedElementsIds = this.filteredLockedElements.map(
                opt => opt.id
            );

            let enabledSectionOptions = this.sectionOptions.filter(
                sectionOpt => {
                    return !filteredLockedElementsIds.includes(sectionOpt.id);
                }
            );
            this.section = enabledSectionOptions?.[0];
        },
        filterDiagnosesSection(isSetSection = true) {
            if (this.isSoapContentHasDiagnoses) {
                this.sectionOptions = filter(this.sectionOptions, opt => {
                    return opt.id !== "fm_soap_diagnoses_comment";
                });

                if (
                    this.section &&
                    this.section.id === "fm_soap_diagnoses_comment" &&
                    isSetSection
                ) {
                    this.setSection();
                }
            }
        },
        async addSnippet() {
            let self = this;

            let valid = await this.$validator.validateAll();
            if (!valid) {
                return false;
            }
            this.isLoading = true;

            const params = {
                element: this.section.id,
                snippetId: this.content.id,
                snippetTimestamp: parseInt(this._now().toSeconds())
            };

            SoapServices.soapFieldSave(this.soap.id, params)
                .then(response => {
                    if (response && response.code == 200) {
                        self._message("Record saved.");
                        this.soapSocket.emit("medical-note", {
                            field: this.section.id,
                            value: response.response.value,
                            action: "update",
                            by: this.$root.currentActor.id,
                            name: this.$root.currentActor.fullname
                        });
                    } else {
                        throw new Error("Unable to add snippet");
                    }
                })
                .catch(error => {
                    self._message("Error : Unable to add snippet", "danger");
                    console.error(error);
                })
                .finally(() => {
                    self.isLoading = false;
                    self.isOpen = false;
                });
        }
    }
};
</script>
